import { getLocale, translate, translateRich, useLocale } from "../localization";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AppTextField } from "./ui";
import "./SystemMapEditor.scss";

const DEFAULT_CANVAS = {
  width: 420,
  height: 236,
};
const DEFAULT_GRID = {
  size: 10,
  minNodeSpacing: 36,
};
const MIN_CANVAS_WIDTH = 180;
const MAX_CANVAS_WIDTH = 1200;
const MIN_CANVAS_HEIGHT = 120;
const MAX_CANVAS_HEIGHT = 900;
const MIN_GRID_SIZE = 4;
const MAX_GRID_SIZE = 160;
const MAX_NODE_SPACING = 260;
const HORIZONTAL_PADDING = 92;
const VERTICAL_PADDING = 72;
const NODE_RADIUS = 18;
const WORLD_UNIT_LABELS = {
  0: "fm",
  1: "pm",
  2: "A",
  3: "nm",
  4: "um",
  5: "mm",
  6: "cm",
  7: "m",
  8: "km",
  9: "Mm",
  10: "Gm",
  11: "Tm",
  12: "Pm",
  13: "Em",
  14: "parsec",
  15: "megaParsec",
  16: "lightYear",
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundCoordinate = (value) => Number(clamp(value, 0, 1).toFixed(4));

const normalizeInteger = (value, fallback, min, max) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  return Math.round(clamp(number, min, max));
};

const getEnvironmentId = (environment) =>
  environment?.id || environment?.environmentId || environment?.environmentID || "";

const getEnvironmentName = (environment, index) =>
  environment?.environmentName ||
  environment?.name ||
  environment?.metadata?.name ||
  translate("Environment {value0}", {
  value0: index + 1
});

const parseJsonObject = (value) => {
  if (!value || typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const objectValues = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const getParsedWebMap = (webMap) =>
  typeof webMap === "string" ? parseJsonObject(webMap) : webMap;

const normalizeCanvas = (webMap) => {
  const parsedWebMap = getParsedWebMap(webMap);
  const canvas = parsedWebMap?.canvas || {};

  return {
    width: normalizeInteger(
      canvas.width ?? parsedWebMap?.width,
      DEFAULT_CANVAS.width,
      MIN_CANVAS_WIDTH,
      MAX_CANVAS_WIDTH
    ),
    height: normalizeInteger(
      canvas.height ?? parsedWebMap?.height,
      DEFAULT_CANVAS.height,
      MIN_CANVAS_HEIGHT,
      MAX_CANVAS_HEIGHT
    ),
  };
};

const normalizeGrid = (webMap) => {
  const parsedWebMap = getParsedWebMap(webMap);
  const grid = parsedWebMap?.grid || {};

  return {
    size: normalizeInteger(
      grid.size,
      DEFAULT_GRID.size,
      MIN_GRID_SIZE,
      MAX_GRID_SIZE
    ),
    minNodeSpacing: normalizeInteger(
      grid.minNodeSpacing,
      DEFAULT_GRID.minNodeSpacing,
      0,
      MAX_NODE_SPACING
    ),
  };
};

const getSystemEnvironments = (system) => {
  const directEnvironments = objectValues(system?.environments);

  if (directEnvironments.length > 0) {
    return directEnvironments.filter((environment) => getEnvironmentId(environment));
  }

  const embeddedSystem = parseJsonObject(system?.json);
  const embeddedEnvironments = objectValues(embeddedSystem?.environments);

  if (embeddedEnvironments.length > 0) {
    return embeddedEnvironments.filter((environment) => getEnvironmentId(environment));
  }

  return objectValues(system?.environmentIDs).map((environmentId, index) => ({
    id: environmentId,
    environmentName: `Environment ${index + 1}`,
    magnification: index + 1,
  }));
};

const getEnvironmentElements = (environment) => {
  const sources = [
    environment?.elements,
    environment?.elementJsons,
    environment?.elementDatas,
  ];

  for (const source of sources) {
    const elements = objectValues(source);
    if (elements.length > 0) {
      return elements;
    }
  }

  return [];
};

const getTargetEnvironmentId = (element) =>
  element?.targetEnvironmentId ||
  element?.targetEnvironmentID ||
  element?.targetEnvironment?.id ||
  element?.targetEnvironmentDataID ||
  "";

const getEnvironmentMagnification = (environment, index) => {
  const candidates = [
    environment?.magnification,
    environment?.worldUnit?.value,
    index + 1,
  ];

  const value = candidates.find((candidate) => Number.isFinite(Number(candidate)));
  return Number(value) || index + 1;
};

const getWorldUnitLabel = (worldUnit = {}) => {
  const value = worldUnit.value;
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const units = WORLD_UNIT_LABELS[worldUnit.units] || "";
  return `${value}${units}`;
};

const getEnvironmentScaleLabel = (environment, index) => {
  const rawMagnification = environment?.magnification;
  const magnification = Number(rawMagnification);
  const worldUnitLabel = getWorldUnitLabel(environment?.worldUnit);

  if (
    rawMagnification !== undefined &&
    rawMagnification !== null &&
    rawMagnification !== "" &&
    Number.isFinite(magnification)
  ) {
    if (magnification < 1 && worldUnitLabel) {
      return worldUnitLabel;
    }

    return `${magnification.toLocaleString(getLocale())}x`;
  }

  return worldUnitLabel || `${getEnvironmentMagnification(environment, index).toLocaleString(getLocale())}x`;
};

const createProceduralNodes = (environments, canvas = DEFAULT_CANVAS) => {
  if (!environments.length) {
    return {};
  }

  const groups = new Map();
  environments.forEach((environment, index) => {
    const magnification = getEnvironmentMagnification(environment, index);
    const key = String(magnification);
    const group = groups.get(key) || {
      magnification,
      environments: [],
    };

    group.environments.push(environment);
    groups.set(key, group);
  });

  const sortedGroups = [...groups.values()].sort(
    (a, b) => a.magnification - b.magnification
  );
  const horizontalPadding = Math.min(HORIZONTAL_PADDING, canvas.width * 0.2);
  const verticalPadding = Math.min(VERTICAL_PADDING, canvas.height * 0.24);
  const levelSpan =
    sortedGroups.length > 1
      ? (canvas.height - verticalPadding * 2) / (sortedGroups.length - 1)
      : 0;
  const nodes = {};

  sortedGroups.forEach((group, levelIndex) => {
    const y =
      sortedGroups.length > 1
        ? canvas.height - verticalPadding - levelIndex * levelSpan
        : canvas.height / 2;
    const horizontalSpan = canvas.width - horizontalPadding * 2;

    group.environments.forEach((environment, index) => {
      const x =
        horizontalPadding +
        ((index + 1) * horizontalSpan) / (group.environments.length + 1);
      const environmentId = getEnvironmentId(environment);

      if (environmentId) {
        nodes[environmentId] = {
          x: roundCoordinate(x / canvas.width),
          y: roundCoordinate(y / canvas.height),
        };
      }
    });
  });

  return nodes;
};

const normalizeNodeMap = (webMap) => {
  const parsedWebMap = getParsedWebMap(webMap);
  const sourceNodes =
    parsedWebMap?.nodes ||
    parsedWebMap?.environments ||
    (parsedWebMap && !parsedWebMap.version ? parsedWebMap : null);

  if (Array.isArray(sourceNodes)) {
    return sourceNodes.reduce((nodes, node) => {
      const environmentId = getEnvironmentId(node);
      const x = Number(node?.x);
      const y = Number(node?.y);

      if (environmentId && Number.isFinite(x) && Number.isFinite(y)) {
        nodes[environmentId] = {
          x: roundCoordinate(x),
          y: roundCoordinate(y),
        };
      }

      return nodes;
    }, {});
  }

  if (!sourceNodes || typeof sourceNodes !== "object") {
    return {};
  }

  return Object.entries(sourceNodes).reduce((nodes, [environmentId, node]) => {
    const x = Number(node?.x);
    const y = Number(node?.y);

    if (environmentId && Number.isFinite(x) && Number.isFinite(y)) {
      nodes[environmentId] = {
        x: roundCoordinate(x),
        y: roundCoordinate(y),
      };
    }

    return nodes;
  }, {});
};

const nodeMapToArray = (nodes) =>
  Object.entries(nodes).map(([id, node]) => ({
    id,
    x: roundCoordinate(node.x),
    y: roundCoordinate(node.y),
  }));

const buildWebMap = (nodes, canvas = DEFAULT_CANVAS, grid = DEFAULT_GRID) => ({
  version: 1,
  coordinateSpace: "normalized",
  canvas: {
    width: normalizeInteger(
      canvas.width,
      DEFAULT_CANVAS.width,
      MIN_CANVAS_WIDTH,
      MAX_CANVAS_WIDTH
    ),
    height: normalizeInteger(
      canvas.height,
      DEFAULT_CANVAS.height,
      MIN_CANVAS_HEIGHT,
      MAX_CANVAS_HEIGHT
    ),
  },
  grid: {
    size: normalizeInteger(
      grid.size,
      DEFAULT_GRID.size,
      MIN_GRID_SIZE,
      MAX_GRID_SIZE
    ),
    minNodeSpacing: normalizeInteger(
      grid.minNodeSpacing,
      DEFAULT_GRID.minNodeSpacing,
      0,
      MAX_NODE_SPACING
    ),
  },
  nodes: nodeMapToArray(nodes),
});

const getPointerPosition = (event, svgElement) => {
  const rect = svgElement.getBoundingClientRect();
  const x = (event.clientX - rect.left) / rect.width;
  const y = (event.clientY - rect.top) / rect.height;

  return {
    x: roundCoordinate(x),
    y: roundCoordinate(y),
  };
};

const toCanvasPoint = (position, canvas) => ({
  x: position.x * canvas.width,
  y: position.y * canvas.height,
});

const getCanvasPoints = (nodes, canvas) =>
  Object.entries(nodes || {}).map(([id, node]) => ({
    id,
    x: node.x * canvas.width,
    y: node.y * canvas.height,
  }));

const pointsToNodeMap = (points, canvas) =>
  points.reduce((nodes, point) => {
    nodes[point.id] = toNormalizedPosition(point, canvas);
    return nodes;
  }, {});

const toNormalizedPosition = (point, canvas) => ({
  x: roundCoordinate(point.x / canvas.width),
  y: roundCoordinate(point.y / canvas.height),
});

const getMinimumCanvasForPoints = (points) => {
  const maxX = points.length ? Math.max(...points.map((point) => point.x)) : 0;
  const maxY = points.length ? Math.max(...points.map((point) => point.y)) : 0;

  return {
    width: Math.max(MIN_CANVAS_WIDTH, Math.ceil(maxX)),
    height: Math.max(MIN_CANVAS_HEIGHT, Math.ceil(maxY)),
  };
};

const getMinimumCanvasForNodes = (nodes, canvas) =>
  getMinimumCanvasForPoints(getCanvasPoints(nodes, canvas));

const normalizeCanvasForPoints = (nextCanvas, points, fallbackCanvas) => {
  const minimumCanvas = getMinimumCanvasForPoints(points);

  return {
    width: normalizeInteger(
      nextCanvas.width,
      fallbackCanvas.width,
      minimumCanvas.width,
      MAX_CANVAS_WIDTH
    ),
    height: normalizeInteger(
      nextCanvas.height,
      fallbackCanvas.height,
      minimumCanvas.height,
      MAX_CANVAS_HEIGHT
    ),
  };
};

const normalizeCanvasForNodes = (nextCanvas, nodes, previousCanvas) => {
  const minimumCanvas = getMinimumCanvasForNodes(nodes, previousCanvas);

  return {
    width: normalizeInteger(
      nextCanvas.width,
      previousCanvas.width,
      minimumCanvas.width,
      MAX_CANVAS_WIDTH
    ),
    height: normalizeInteger(
      nextCanvas.height,
      previousCanvas.height,
      minimumCanvas.height,
      MAX_CANVAS_HEIGHT
    ),
  };
};

const resizeNodesForCanvas = (nodes, previousCanvas, nextCanvas) =>
  Object.entries(nodes).reduce((nextNodes, [environmentId, node]) => {
    nextNodes[environmentId] = toNormalizedPosition(
      toCanvasPoint(node, previousCanvas),
      nextCanvas
    );
    return nextNodes;
  }, {});

const snapPointToGrid = (point, canvas, gridSize) => {
  const snapSize = Math.max(gridSize, 1);

  return {
    x: clamp(Math.round(point.x / snapSize) * snapSize, 0, canvas.width),
    y: clamp(Math.round(point.y / snapSize) * snapSize, 0, canvas.height),
  };
};

const getSnappedPosition = (rawPosition, canvas, grid) => {
  const gridSize = Math.max(grid.size, 1);
  const rawPoint = toCanvasPoint(rawPosition, canvas);
  const snappedPoint = snapPointToGrid(rawPoint, canvas, gridSize);

  return toNormalizedPosition(snappedPoint, canvas);
};

const SystemMapEditor = ({ system, value, onChange }) => {
  const locale = useLocale();
  const svgRef = useRef(null);
  const resizeStateRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);
  const draftNodesRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);
  const [draggingId, setDraggingId] = useState(null);
  const [resizingMode, setResizingMode] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [draftNodes, setDraftNodes] = useState(null);
  const [controlValues, setControlValues] = useState({
    width: "",
    height: "",
    gridSize: "",
  });
  const patternId = `system-map-grid-${system?.id || "editor"}`;
  const environments = useMemo(() => getSystemEnvironments(system), [system, locale]);
  const environmentIds = useMemo(
    () => new Set(environments.map((environment) => getEnvironmentId(environment))),
    [environments, locale]
  );
  const canvas = useMemo(() => normalizeCanvas(localValue), [localValue, locale]);
  const grid = useMemo(() => normalizeGrid(localValue), [localValue, locale]);
  const savedNodes = useMemo(() => normalizeNodeMap(localValue), [localValue, locale]);
  const proceduralNodes = useMemo(
    () => createProceduralNodes(environments, canvas),
    [canvas, environments, locale]
  );
  const displayNodes = useMemo(() => {
    const nodes = {};

    environments.forEach((environment) => {
      const environmentId = getEnvironmentId(environment);
      if (!environmentId) return;

      nodes[environmentId] =
        savedNodes[environmentId] || proceduralNodes[environmentId] || {
          x: 0.5,
          y: 0.5,
        };
    });

    return nodes;
  }, [environments, proceduralNodes, savedNodes, locale]);
  const activeCanvas = resizePreview?.canvas || canvas;
  const activeNodes = draftNodes || resizePreview?.nodes || displayNodes;
  const hasCustomMap = Object.keys(savedNodes).some((environmentId) =>
    environmentIds.has(environmentId)
  );
  const connectors = useMemo(() => {
    return environments.flatMap((environment) => {
      const sourceId = getEnvironmentId(environment);

      return getEnvironmentElements(environment)
        .map((element) => ({
          sourceId,
          targetId: getTargetEnvironmentId(element),
        }))
        .filter(
          (connector) =>
            connector.sourceId &&
            connector.targetId &&
            connector.sourceId !== connector.targetId &&
            environmentIds.has(connector.targetId)
        );
    });
  }, [environmentIds, environments, locale]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    setControlValues({
      width: String(canvas.width),
      height: String(canvas.height),
      gridSize: String(grid.size),
    });
  }, [canvas.height, canvas.width, grid.size]);

  const commitMap = (
    nextNodes = displayNodes,
    nextCanvas = canvas,
    nextGrid = grid
  ) => {
    const nextMap = buildWebMap(nextNodes, nextCanvas, nextGrid);
    setLocalValue(nextMap);
    onChange(nextMap);
  };

  const commitNodePosition = (environmentId, nextPosition, sourceNodes) => {
    const snappedPosition = getSnappedPosition(
      nextPosition,
      canvas,
      grid
    );
    const nextNodes = environments.reduce((nodes, environment) => {
      const id = getEnvironmentId(environment);
      if (!id) return nodes;

      nodes[id] = id === environmentId ? snappedPosition : sourceNodes[id];
      return nodes;
    }, {});

    commitMap(nextNodes);
  };

  const handlePointerDown = (event, environmentId) => {
    if (!svgRef.current) return;
    const currentPosition = activeNodes[environmentId];
    if (!currentPosition) return;

    event.preventDefault();
    const pointerPosition = getPointerPosition(event, svgRef.current);
    dragOffsetRef.current = {
      x: currentPosition.x - pointerPosition.x,
      y: currentPosition.y - pointerPosition.y,
    };
    dragMovedRef.current = false;
    draftNodesRef.current = activeNodes;
    setDraftNodes(activeNodes);
    svgRef.current.setPointerCapture?.(event.pointerId);
    setDraggingId(environmentId);
  };

  const handlePointerMove = (event) => {
    if (!draggingId || !svgRef.current) return;
    const pointerPosition = getPointerPosition(event, svgRef.current);
    const nextPosition = {
      x: roundCoordinate(pointerPosition.x + dragOffsetRef.current.x),
      y: roundCoordinate(pointerPosition.y + dragOffsetRef.current.y),
    };

    dragMovedRef.current = true;
    setDraftNodes((currentDraftNodes) => {
      const snappedPosition = getSnappedPosition(nextPosition, canvas, grid);
      const nextDraftNodes = {
        ...(currentDraftNodes || activeNodes),
        [draggingId]: snappedPosition,
      };
      draftNodesRef.current = nextDraftNodes;
      return nextDraftNodes;
    });
  };

  const handlePointerUp = (event) => {
    const nodesToCommit = draftNodesRef.current || draftNodes;
    if (draggingId && nodesToCommit && dragMovedRef.current) {
      commitNodePosition(draggingId, nodesToCommit[draggingId], nodesToCommit);
    }

    svgRef.current?.releasePointerCapture?.(event.pointerId);
    setDraggingId(null);
    setDraftNodes(null);
    draftNodesRef.current = null;
    dragMovedRef.current = false;
  };

  const resetToGeneratedLayout = () => {
    setLocalValue(null);
    onChange(null);
    setDraggingId(null);
  };

  const applyGeneratedLayout = () => {
    commitMap(proceduralNodes);
    setDraggingId(null);
  };

  const handleCanvasChange = (field, nextValue) => {
    const requestedCanvas = {
      ...canvas,
      [field]: Number(nextValue),
    };
    const nextCanvas = normalizeCanvasForNodes(
      requestedCanvas,
      displayNodes,
      canvas
    );
    const nextNodes = resizeNodesForCanvas(displayNodes, canvas, nextCanvas);

    commitMap(nextNodes, nextCanvas, grid);
  };

  const handleGridChange = (field, nextValue) => {
    const nextGrid = {
      ...grid,
      [field]: normalizeInteger(
        nextValue,
        grid[field],
        field === "size" ? MIN_GRID_SIZE : 0,
        field === "size" ? MAX_GRID_SIZE : MAX_NODE_SPACING
      ),
    };

    commitMap(displayNodes, canvas, nextGrid);
  };

  const getResizePreview = (event) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState) return null;

    const gridSize = Math.max(grid.size, 1);
    const sourcePoints = getCanvasPoints(resizeState.nodes, resizeState.canvas);
    const minPointX = sourcePoints.length
      ? Math.min(...sourcePoints.map((point) => point.x))
      : 0;
    const minPointY = sourcePoints.length
      ? Math.min(...sourcePoints.map((point) => point.y))
      : 0;
    const maxWidthOffset = resizeState.canvas.width - MAX_CANVAS_WIDTH;
    const maxHeightOffset = resizeState.canvas.height - MAX_CANVAS_HEIGHT;
    const maxWestOffset = Math.min(
      resizeState.canvas.width - MIN_CANVAS_WIDTH,
      minPointX
    );
    const maxNorthOffset = Math.min(
      resizeState.canvas.height - MIN_CANVAS_HEIGHT,
      minPointY
    );
    const pointerDeltaX = (event.clientX - resizeState.clientX) * resizeState.scaleX;
    const pointerDeltaY = (event.clientY - resizeState.clientY) * resizeState.scaleY;
    const snappedDeltaX = Math.round(pointerDeltaX / gridSize) * gridSize;
    const snappedDeltaY = Math.round(pointerDeltaY / gridSize) * gridSize;
    const requestedCanvas = { ...resizeState.canvas };
    let offsetX = 0;
    let offsetY = 0;

    if (resizeState.mode.includes("west")) {
      offsetX = clamp(snappedDeltaX, maxWidthOffset, maxWestOffset);
      requestedCanvas.width = resizeState.canvas.width - offsetX;
    } else if (resizeState.mode.includes("east")) {
      requestedCanvas.width =
        resizeState.canvas.width + snappedDeltaX;
      requestedCanvas.width =
        Math.round(requestedCanvas.width / gridSize) * gridSize;
    }

    if (resizeState.mode.includes("north")) {
      offsetY = clamp(snappedDeltaY, maxHeightOffset, maxNorthOffset);
      requestedCanvas.height = resizeState.canvas.height - offsetY;
    } else if (resizeState.mode.includes("south")) {
      requestedCanvas.height =
        resizeState.canvas.height + snappedDeltaY;
      requestedCanvas.height =
        Math.round(requestedCanvas.height / gridSize) * gridSize;
    }

    const shiftedPoints = sourcePoints.map((point) => ({
      ...point,
      x: point.x - offsetX,
      y: point.y - offsetY,
    }));
    const nextCanvas = normalizeCanvasForPoints(
      requestedCanvas,
      shiftedPoints,
      resizeState.canvas
    );

    return {
      canvas: nextCanvas,
      nodes: pointsToNodeMap(shiftedPoints, nextCanvas),
    };
  };

  const handleResizePointerDown = (event, mode) => {
    const stageRect = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!stageRect) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeStateRef.current = {
      mode,
      clientX: event.clientX,
      clientY: event.clientY,
      canvas,
      nodes: displayNodes,
      scaleX: canvas.width / stageRect.width,
      scaleY: canvas.height / stageRect.height,
    };
    setResizingMode(mode);
    setResizePreview({
      canvas,
      nodes: displayNodes,
    });
  };

  const handleResizePointerMove = (event) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState) return;

    const nextPreview = getResizePreview(event);
    if (!nextPreview) return;

    setResizePreview(nextPreview);
    setControlValues((currentValues) => ({
      ...currentValues,
      width: String(nextPreview.canvas.width),
      height: String(nextPreview.canvas.height),
    }));
  };

  const handleResizePointerUp = (event) => {
    const nextPreview = getResizePreview(event);

    if (nextPreview) {
      commitMap(nextPreview.nodes, nextPreview.canvas, grid);
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    resizeStateRef.current = null;
    setResizePreview(null);
    setResizingMode(null);
  };

  const handleControlValueChange = (field, nextValue) => {
    setControlValues((currentValues) => ({
      ...currentValues,
      [field]: nextValue,
    }));
  };

  const commitControlValue = (field) => {
    if (field === "width" || field === "height") {
      handleCanvasChange(field, controlValues[field]);
      return;
    }

    if (field === "gridSize") {
      handleGridChange("size", controlValues.gridSize);
    }
  };

  const handleControlKeyDown = (event) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  return (
    <div className="systemMapEditor">
      <div className="systemMapEditorHeader">
        <div className="systemMapEditorTitleGroup">
          <div className="systemMapEditorTitle">{translate("Web map")}</div>
          <div className="systemMapEditorMeta">{translateRich("{value0} environments - {value1} - {value2}x{value3}", {
  value0: environments.length,
  value1: hasCustomMap ? translate("Custom") : translate("Generated"),
  value2: activeCanvas.width,
  value3: activeCanvas.height
})}</div>
        </div>
        <div className="systemMapEditorTools">
          <Tooltip title={translate("Save generated layout as custom")}>
            <span>
              <IconButton
                aria-label={translate("Save generated layout as custom")}
                size="small"
                onClick={applyGeneratedLayout}
                disabled={!environments.length}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={translate("Use generated layout")}>
            <span>
              <IconButton
                aria-label={translate("Use generated layout")}
                size="small"
                onClick={resetToGeneratedLayout}
                disabled={!hasCustomMap}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="systemMapEditorControls">
        <AppTextField
          label={translate("Width")}
          type="number"
          value={controlValues.width}
          onChange={(event) =>
            handleControlValueChange("width", event.target.value)
          }
          onBlur={() => commitControlValue("width")}
          onKeyDown={handleControlKeyDown}
          inputProps={{
            min: MIN_CANVAS_WIDTH,
            max: MAX_CANVAS_WIDTH,
            step: 20,
          }}
        />
        <AppTextField
          label={translate("Height")}
          type="number"
          value={controlValues.height}
          onChange={(event) =>
            handleControlValueChange("height", event.target.value)
          }
          onBlur={() => commitControlValue("height")}
          onKeyDown={handleControlKeyDown}
          inputProps={{
            min: MIN_CANVAS_HEIGHT,
            max: MAX_CANVAS_HEIGHT,
            step: 20,
          }}
        />
        <AppTextField
          label={translate("Grid")}
          type="number"
          value={controlValues.gridSize}
          onChange={(event) =>
            handleControlValueChange("gridSize", event.target.value)
          }
          onBlur={() => commitControlValue("gridSize")}
          onKeyDown={handleControlKeyDown}
          inputProps={{
            min: MIN_GRID_SIZE,
            max: MAX_GRID_SIZE,
            step: 4,
          }}
        />
      </div>

      <div
        className={`systemMapEditorStage ${resizingMode ? "isResizing" : ""}`}
        style={{
          aspectRatio: `${activeCanvas.width} / ${activeCanvas.height}`,
          width: `${activeCanvas.width}px`,
          maxWidth: "100%",
        }}
      >
        <svg
          ref={svgRef}
          className="systemMapEditorCanvas"
          viewBox={`0 0 ${activeCanvas.width} ${activeCanvas.height}`}
          role="img"
          aria-label={translate("Editable web map")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <defs>
            <pattern
              id={`system-map-grid-${system?.id || "editor"}`}
              width={grid.size}
              height={grid.size}
              patternUnits="userSpaceOnUse"
            >
              <path
                className="systemMapEditorGridLine"
                d={`M ${grid.size} 0 L 0 0 0 ${grid.size}`}
                fill="none"
                stroke="currentColor"
              />
              <circle
                className="systemMapEditorGridDot"
                cx="0"
                cy="0"
                r="1"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            className="systemMapEditorGrid"
            width={activeCanvas.width}
            height={activeCanvas.height}
            fill={`url(#${patternId})`}
          />
          {connectors.map((connector, index) => {
            const source = activeNodes[connector.sourceId];
            const target = activeNodes[connector.targetId];
            if (!source || !target) return null;

            return (
              <line
                key={`${connector.sourceId}-${connector.targetId}-${index}`}
                className="systemMapEditorEdge"
                x1={source.x * activeCanvas.width}
                y1={source.y * activeCanvas.height}
                x2={target.x * activeCanvas.width}
                y2={target.y * activeCanvas.height}
              />
            );
          })}
          {environments.map((environment, index) => {
            const environmentId = getEnvironmentId(environment);
            const position = activeNodes[environmentId];
            if (!environmentId || !position) return null;
            const environmentName = getEnvironmentName(environment, index);
            const environmentLabel = `${environmentName} (${getEnvironmentScaleLabel(
              environment,
              index
            )})`;

            return (
              <g
                key={environmentId}
                className={`systemMapEditorNode ${
                  draggingId === environmentId ? "isDragging" : ""
                }`}
                transform={`translate(${position.x * activeCanvas.width} ${
                  position.y * activeCanvas.height
                })`}
                onPointerDown={(event) => handlePointerDown(event, environmentId)}
              >
                <title>{environmentLabel}</title>
                <circle r={NODE_RADIUS} />
                <text className="systemMapEditorNodeNumber" y="5">
                  {index + 1}
                </text>
                <text
                  className="systemMapEditorNodeLabel"
                  x={NODE_RADIUS + 12}
                  y="5"
                >
                  {environmentLabel}
                </text>
              </g>
            );
          })}
        </svg>
        {[
          "north",
          "east",
          "south",
          "west",
          "north-east",
          "south-east",
          "south-west",
          "north-west",
        ].map((mode) => (
          <div
            key={mode}
            className={`systemMapEditorResizeHandle systemMapEditorResizeHandle--${mode}`}
            role="button"
            tabIndex={-1}
            aria-label={translate("Resize map {value0}", {
  value0: mode
})}
            onPointerDown={(event) => handleResizePointerDown(event, mode)}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
          />
        ))}
      </div>

      {environments.length > 0 ? (
        <div className="systemMapEditorLegend">
          {environments.map((environment, index) => (
            <div key={getEnvironmentId(environment)} className="systemMapEditorLegendItem">
              <span>{index + 1}</span>
              {getEnvironmentName(environment, index)}
            </div>
          ))}
        </div>
      ) : (
        <div className="systemMapEditorEmpty">{translate("No environments")}</div>
      )}
    </div>
  );
};

export default SystemMapEditor;
